import React from 'react';
import { toLocalYMD } from '../../../../utils/dateFmt';


const HoursRecordsTable = ({ 
  daysInMonth, 
  selectedCrew, 
  hoursByCrewDate,
  getDayHours, 
  summary,
  currentCrewMember,
  selectedMonth
}) => {
  return (
    <div className="border-t border-gray-200">
      <div className="p-6 overflow-x-auto">
        <div className="min-w-max">
          {/* Print Header */}
          <div className="hidden print:block mb-4">
            <h2 className="text-xl font-bold">Hours of Rest Record</h2>
            <p className="text-lg">{currentCrewMember?.full_name || currentCrewMember?.name}</p>
            <p>{selectedMonth.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}</p>
          </div>

          {/* Table */}
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className="border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 bg-gray-50">
                  Date
                </th>
                {Array.from({ length: 24 }, (_, i) => (
                  <th key={i} className="border border-gray-300 px-1 py-1 text-xs font-medium text-gray-700 bg-gray-50 w-8 text-center">
                    {i.toString().padStart(2, '0')}
                  </th>
                ))}
                <th className="border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 bg-gray-50">
                  Total
                </th>
              </tr>
            </thead>
            <tbody>
              {daysInMonth.map(date => {
                const dateStr = toLocalYMD(date);     // LOCAL date, no UTC shift
                const record = hoursByCrewDate?.[selectedCrew]?.[dateStr];
                const dayData = record ? getDayHours(record) : { cells: Array(24).fill({ type: 'rest', partial: false, ratio: 1 }), workHours: 0, restHours: 24, isCompliant: true };
                const dayName = date.toLocaleDateString('en-GB', { weekday: 'short' });
                const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                
                return (
                  <tr key={toLocalYMD(date)} className={isWeekend ? 'bg-gray-50' : ''}>
                    <td className="border border-gray-300 px-2 py-1 text-xs font-medium">
                      {dayName} {date.getDate()}
                    </td>
                    {dayData.cells.map((cell, i) => {
                      // Check if this is a day off (no work hours at all)
                      const isDayOff = dayData.workHours === 0 && dayData.restHours === 24;
                      
                      const bgColor = isDayOff 
                        ? 'bg-gray-50' // Empty appearance for days off
                        : cell.type === 'rest' 
                        ? (cell.partial ? 'bg-gradient-to-br from-green-100 to-red-100' : 'bg-green-100')
                        : (cell.partial ? 'bg-gradient-to-br from-red-100 to-green-100' : 'bg-red-100');
                      
                      const textColor = isDayOff 
                        ? 'text-gray-400'
                        : cell.type === 'rest' ? 'text-green-700' : 'text-red-700';
                      
                      const displayText = isDayOff ? '' : (cell.type === 'rest' ? 'R' : 'W');
                      
                      // Only show diagonal for genuinely partial hours (not near 0 or 1)
                      const showDiagonal = cell.partial && !isDayOff && cell.ratio > 0.1 && cell.ratio < 0.9;
                      
                      return (
                        <td 
                          key={i} 
                          className={`border border-gray-300 text-xs text-center font-medium relative overflow-hidden h-4 ${bgColor} ${textColor}`}
                          title={isDayOff ? 'Day off - 24h rest' : cell.partial ? `${Math.round(cell.ratio * 60)} min ${cell.type}` : cell.type}
                        >
                          {showDiagonal ? (
                            <>
                              <div className="absolute inset-0">
                                <div className="absolute top-0 left-0 w-full h-full">
                                  <div className={`absolute top-0 left-0 w-0 h-0 border-l-[16px] border-t-[16px] ${
                                    cell.type === 'work' ? 'border-l-transparent border-t-red-100' : 'border-l-transparent border-t-green-100'
                                  }`}></div>
                                  <div className={`absolute bottom-0 right-0 w-0 h-0 border-r-[16px] border-b-[16px] ${
                                    cell.type === 'work' ? 'border-r-transparent border-b-green-100' : 'border-r-transparent border-b-red-100'
                                  }`}></div>
                                </div>
                                <svg className="absolute inset-0 w-full h-full">
                                  <line x1="0" y1="0" x2="100%" y2="100%" stroke="#374151" strokeWidth="1" />
                                </svg>
                              </div>
                              <span className="relative z-10 text-[10px]">{displayText}</span>
                            </>
                          ) : (
                            <span className="text-[10px]">{displayText}</span>
                          )}
                        </td>
                      );
                    })}
                    <td className={`border border-gray-300 px-2 py-1 text-xs font-medium text-center ${
                      !dayData.isCompliant && dayData.workHours > 0 ? 'bg-red-100 text-red-700' : ''
                    }`}>
                      {dayData.workHours > 0 ? `${dayData.workHours.toFixed(1)}h` : dayData.restHours === 24 ? '24h' : '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="26" className="border border-gray-300 px-4 py-2 bg-gray-50">
                  <div className="flex justify-between items-center text-sm">
                    <span>Total Work: <strong>{summary.totalWork.toFixed(0)}h</strong></span>
                    <span>Total Rest: <strong>{summary.totalRest.toFixed(0)}h</strong></span>
                    <span className={summary.violations > 0 ? 'text-red-600' : 'text-green-600'}>
                      Violations: <strong>{summary.violations}</strong>
                    </span>
                  </div>
                </td>
              </tr>
            </tfoot>
          </table>

          {/* Legend */}
          <div className="mt-4 flex items-center gap-4 text-xs">
            <span className="flex items-center gap-1">
              <span className="w-4 h-4 bg-red-100 border border-gray-300"></span>
              W = Work
            </span>
            <span className="flex items-center gap-1">
              <span className="w-4 h-4 bg-green-100 border border-gray-300"></span>
              R = Rest
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HoursRecordsTable;